import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { OktaService } from '../../services/okta/okta.service';
import { environment } from '../../../environments/environment';
import { AuthorizationService } from '../../services/read-write-role/authorization.service';
import { filter, firstValueFrom, take, timeout } from 'rxjs';

const route = 'login-error';
const PERMISSION_TIMEOUT = 10000; // 10 seconds timeout

export const AuthGuard: CanActivateFn = async (route, state) => {
  try {
    const router = inject(Router);
    const isAuthenticated = await inject(OktaService).isAuthenticated();
    if (!isAuthenticated) {
      await router.navigate([environment.STATEMENT_MESSAGING.LOGIN_ERROR]);
    }
    return isAuthenticated;
  } catch (error) {
    return false;
  }
};

/**
 * Wait for Okta groups to be loaded and permissions to be calculated
 */
async function waitForPermissionsToLoad(authService: AuthorizationService): Promise<void> {
  try {
    const oktaService = inject(OktaService);

    //  STEP 1: Wait for Okta groups to be loaded (non-empty array)
    await firstValueFrom(
      oktaService.getOktaGroupsObservable().pipe(
        filter((groups) => {
          const hasGroups = Array.isArray(groups) && groups.length > 0;
          return hasGroups;
        }),
        timeout(PERMISSION_TIMEOUT),
        take(1),
      ),
    );
    //  STEP 2: Small delay to ensure permissions are calculated
    await new Promise((resolve) => setTimeout(resolve, 100));
  } catch (error) {
    handlePermissionLoadError(error as Error);
    throw error;
  }
}

/**
 * Handle permission loading errors
 */
function handlePermissionLoadError(error: Error): void {
  if (error.name === 'TimeoutError') {
    console.error('Permission loading timed out');
  } else {
    console.error('Error loading permissions:', error);
  }
}

/**
 * Handle guard permission check
 */
async function checkPermissionAndNavigate(
  permissionCheck: () => boolean,
  permissionName: string,
  authService: AuthorizationService,
  router: Router,
): Promise<boolean> {
  try {
    await waitForPermissionsToLoad(authService);

    const hasPermission = permissionCheck();
    if (hasPermission) {
      return true;
    }

    return await handleAccessDenied(permissionName, router);
  } catch (error) {
    return await handleGuardError(permissionName, error, router);
  }
}

/**
 * Handle access denied scenario
 */
async function handleAccessDenied(permissionName: string, router: Router): Promise<boolean> {
  await router.navigate([route], {
    queryParams: {
      reason: 'access-denied',
    },
  });
  return false;
}

/**
 * Handle guard errors
 */
async function handleGuardError(
  permissionName: string,
  error: any,
  router: Router,
): Promise<boolean> {
  await router.navigate([route]);
  return false;
}

/**
 * Guard to protect routes that require view permission
 */
export const canViewGuard: CanActivateFn = async () => {
  const authService = inject(AuthorizationService);
  const router = inject(Router);

  return checkPermissionAndNavigate(() => authService.canView(), 'canView', authService, router);
};

/**
 * Guard to protect routes that require edit permission
 */
export const canEditGuard: CanActivateFn = async () => {
  const authService = inject(AuthorizationService);
  const router = inject(Router);

  return checkPermissionAndNavigate(() => authService.canEdit(), 'canEdit', authService, router);
};

/**
 * Guard to protect routes that require create permission
 */
export const canCreateGuard: CanActivateFn = async () => {
  const authService = inject(AuthorizationService);
  const router = inject(Router);

  return checkPermissionAndNavigate(
    () => authService.canCreate(),
    'canCreate',
    authService,
    router,
  );
};

/**
 * Guard to protect routes that require delete permission
 */
export const canDeleteGuard: CanActivateFn = async () => {
  const authService = inject(AuthorizationService);
  const router = inject(Router);

  return checkPermissionAndNavigate(
    () => authService.canDelete(),
    'canDelete',
    authService,
    router,
  );
};
